import { useEffect, useMemo, useState } from 'react';
import {
  errorMessage,
  executionTone,
  financeGet,
  num,
  percent,
  won,
  type FinanceCtx,
} from '../financeApi';
import type { FinanceMenuKey } from '../ManageFinanceConsole';
import MonthlyCompareChart from './MonthlyCompareChart';
import ShareBars from './ShareBars';

type MonthTotals = { month: number; income: number; expense: number };

type Summary = {
  year: number;
  monthly: MonthTotals[];
  previousMonthly: MonthTotals[];
  byDepartment: { department: string; total: number }[];
  byCategory: { type: '수입' | '지출'; category: string; total: number }[];
  balance: number;
  openingDate: string | null;
  expenseBudget: number;
  budgetStatus: 'draft' | 'confirmed' | null;
  pending: { requested: number; deptApproved: number; financeApproved: number; unconfirmed: number };
};

function growth(current: number, previous: number) {
  if (previous <= 0) return null;
  return Math.round(((current - previous) / previous) * 1000) / 10;
}

function GrowthText({ value }: { value: number | null }) {
  if (value === null) return <span className="fin-delta">전년 자료 없음</span>;
  const sign = value > 0 ? '▲' : value < 0 ? '▼' : '';
  return (
    <span className={`fin-delta${value > 0 ? ' is-up' : value < 0 ? ' is-down' : ''}`}>
      전년 대비 {sign} {Math.abs(value)}%
    </span>
  );
}

export default function FinanceDashboard({ ctx, onNavigate }: { ctx: FinanceCtx; onNavigate: (key: FinanceMenuKey) => void }) {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    financeGet<Summary>(ctx, '/summary', { year })
      .then(setData)
      .catch((err) => setError(errorMessage(err, '재정 현황을 불러오지 못했습니다.')));
  }, [ctx, year]);

  // 올해면 이번 달까지, 지난 해면 12월까지를 기준으로 본다.
  const throughMonth = year === thisYear ? new Date().getMonth() + 1 : 12;

  const kpi = useMemo(() => {
    if (!data) return null;
    const month = data.monthly[throughMonth - 1];
    const prevMonth = data.previousMonthly[throughMonth - 1];
    const sum = (rows: MonthTotals[], key: 'income' | 'expense') =>
      rows.slice(0, throughMonth).reduce((acc, r) => acc + r[key], 0);
    const ytdExpense = sum(data.monthly, 'expense');
    return {
      month,
      monthIncomeGrowth: growth(month.income, prevMonth.income),
      monthExpenseGrowth: growth(month.expense, prevMonth.expense),
      ytdIncome: sum(data.monthly, 'income'),
      ytdExpense,
      prevYtdIncome: sum(data.previousMonthly, 'income'),
      prevYtdExpense: sum(data.previousMonthly, 'expense'),
      executionRate: percent(ytdExpense, data.expenseBudget),
    };
  }, [data, throughMonth]);

  const pendingTotal = data
    ? data.pending.requested + data.pending.deptApproved + data.pending.financeApproved + data.pending.unconfirmed
    : 0;

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">재정 대시보드</h2>
          <p className="manage-panel-desc">수입·지출·잔액과 예산 집행 현황을 한눈에 확인합니다.</p>
        </div>
        <select className="fin-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {Array.from({ length: 6 }, (_, i) => thisYear - i).map((y) => (
            <option key={y} value={y}>{y}년</option>
          ))}
        </select>
      </div>

      {error && <div className="manage-state manage-state--error">{error}</div>}
      {!error && !data && <div className="manage-state">불러오는 중입니다...</div>}

      {data && kpi && (
        <>
          <div className="fin-kpis">
            <div className="fin-kpi">
              <span>{year}년 {throughMonth}월 총 수입</span>
              <strong className="manage-amt-in">{won(kpi.month.income)}</strong>
              <GrowthText value={kpi.monthIncomeGrowth} />
            </div>
            <div className="fin-kpi">
              <span>{year}년 {throughMonth}월 총 지출</span>
              <strong className="manage-amt-out">{won(kpi.month.expense)}</strong>
              <GrowthText value={kpi.monthExpenseGrowth} />
            </div>
            <div className="fin-kpi fin-kpi--highlight">
              <span>현재 잔액</span>
              <strong>{won(data.balance)}</strong>
              <span className="fin-delta">
                {data.openingDate ? `${data.openingDate} 이월 잔액 기준` : '재정 설정에서 이월 잔액을 입력하세요'}
              </span>
            </div>
            <div className="fin-kpi">
              <span>{year}년 예산 집행률</span>
              {data.expenseBudget > 0 ? (
                <>
                  <strong>{kpi.executionRate}%</strong>
                  <div className="fin-meter">
                    <div
                      className={`fin-meter-fill is-${executionTone(kpi.executionRate, true)}`}
                      style={{ width: `${Math.min(100, kpi.executionRate)}%` }}
                    />
                  </div>
                  <span className="fin-delta">
                    {num(kpi.ytdExpense)} / {num(data.expenseBudget)}원
                    {data.budgetStatus !== 'confirmed' && ' · 미확정 예산'}
                  </span>
                </>
              ) : (
                <>
                  <strong className="fin-muted">-</strong>
                  <button type="button" className="fin-linkbtn" onClick={() => onNavigate('budget')}>연간 예산 편성하기 →</button>
                </>
              )}
            </div>
          </div>

          {pendingTotal > 0 && (
            <div className="fin-todo">
              <b>처리할 일</b>
              {data.pending.requested > 0 && (
                <button type="button" onClick={() => onNavigate('expenseRequests')}>부서장 승인 대기 결의서 {data.pending.requested}건</button>
              )}
              {data.pending.deptApproved > 0 && (
                <button type="button" onClick={() => onNavigate('expenseRequests')}>재정부 승인 대기 결의서 {data.pending.deptApproved}건</button>
              )}
              {data.pending.financeApproved > 0 && (
                <button type="button" onClick={() => onNavigate('expenseRequests')}>지급 대기 결의서 {data.pending.financeApproved}건</button>
              )}
              {data.pending.unconfirmed > 0 && (
                <button type="button" onClick={() => onNavigate('income')}>확인 대기 내역 {data.pending.unconfirmed}건</button>
              )}
            </div>
          )}

          <section className="manage-card-block fin-block">
            <div className="manage-block-head">
              <h3 className="manage-block-title">월별 헌금(수입) 추이</h3>
            </div>
            <MonthlyCompareChart
              year={year}
              current={data.monthly.map((m) => m.income)}
              previous={data.previousMonthly.map((m) => m.income)}
            />
          </section>

          <div className="manage-grid-2col fin-grid-even">
            <section className="manage-card-block fin-block">
              <div className="manage-block-head">
                <h3 className="manage-block-title">{year}년 부서별 지출 비중</h3>
              </div>
              <ShareBars
                rows={data.byDepartment.map((d) => ({ label: d.department, value: d.total }))}
                empty="지출 내역이 없습니다."
              />
            </section>
            <section className="manage-card-block fin-block">
              <div className="manage-block-head">
                <h3 className="manage-block-title">{year}년 헌금 종류별</h3>
              </div>
              <ShareBars
                rows={data.byCategory.filter((c) => c.type === '수입').map((c) => ({ label: c.category, value: c.total }))}
                empty="수입 내역이 없습니다."
              />
            </section>
          </div>

          <section className="manage-card-block fin-block">
            <div className="manage-block-head">
              <h3 className="manage-block-title">전년 대비 증감 (1월~{throughMonth}월 누계)</h3>
            </div>
            <div className="manage-table-wrap">
              <table className="manage-table">
                <thead>
                  <tr>
                    <th>구분</th>
                    <th className="fin-num">{year - 1}년</th>
                    <th className="fin-num">{year}년</th>
                    <th className="fin-num">증감액</th>
                    <th className="fin-num">증감률</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: '수입', current: kpi.ytdIncome, previous: kpi.prevYtdIncome },
                    { label: '지출', current: kpi.ytdExpense, previous: kpi.prevYtdExpense },
                    {
                      label: '수지 차액',
                      current: kpi.ytdIncome - kpi.ytdExpense,
                      previous: kpi.prevYtdIncome - kpi.prevYtdExpense,
                    },
                  ].map((row) => {
                    const rate = growth(row.current, row.previous);
                    return (
                      <tr key={row.label}>
                        <td><b>{row.label}</b></td>
                        <td className="fin-num">{won(row.previous)}</td>
                        <td className="fin-num">{won(row.current)}</td>
                        <td className="fin-num">{row.current - row.previous >= 0 ? '+' : ''}{won(row.current - row.previous)}</td>
                        <td className="fin-num">{rate === null ? '-' : `${rate > 0 ? '+' : ''}${rate}%`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
